// services/ProfessionalService.ts

import axios from 'axios';
import { baseUrl } from '../utils/config';
import UpdateProfessionalForm from 'components/SuperAdmin/Professionals/UpdateProfessionalForm';
import { AuthService } from './AuthService';

export interface CreateProfessionalPayload {
    nom: string;
    prenom: string;
    email: string;
    mot_de_passe: string;
    type_profil: 'Coach' | 'Médecin'; // Assuming only two types, extend if needed
    specialites: string[];
    sous_specialites: string;
    numero_ordre_medecins: string;
    date_naissance: string; // ISO format 'YYYY-MM-DD'
    pays: string;
    telephone: string;
    honoraire_montant: number;
    honoraire_devise: string;
    rib: string;
    banque: string;
    agence: string;
    code_swift: string;
    langues: string[];
    pays_exercice: string[];
    info_supplementaires: string;
}

export interface Professional {
    utilisateur_id: number;
    type_profil: 'Coach' | 'Médecin'; // Assuming only two types, extend if needed
    specialites: string[];
    sous_specialites: string;
    numero_ordre_medecins: string;
    date_naissance: string; // ISO format 'YYYY-MM-DD'
    pays: string;
    telephone: string;
    honoraire_montant: number;
    honoraire_devise: string;
    rib: string;
    banque: string;
    agence: string;
    code_swift: string;
    langues: string[];
    pays_exercice: string[];
    info_supplementaires: string;
}

export interface ProfessionalItem {
    utilisateur_id: number;
    type_profil: 'Coach' | 'Médecin'; // Assuming only two types, extend if needed
    specialites: string[];
    sous_specialites: string;
    numero_ordre_medecins: string;
    date_naissance: string; // ISO format 'YYYY-MM-DD'
    pays: string;
    telephone: string;
    honoraire_montant: number;
    honoraire_devise: string;
    rib: string;
    banque: string;
    agence: string;
    code_swift: string;
    langues: string[];
    pays_exercice: string[];
    info_supplementaires: string;
    id: number;
}
interface Disponibility {
    professionnel_id: number;
    jour_semaine: number;
    heure_debut: string; // ISO time string, e.g., "00:11:08.838Z"
    heure_fin: string;   // ISO time string, e.g., "15:02:26.741Z"
}

interface DisponibilityObj {
    professionnel_id: number;
    jour_semaine: number;
    heure_debut: string;
    heure_fin: string;
    id: number;
}

interface Utilisateur {
    id: number;
    nom: string;
    prenom: string;
    email: string;
    adresse: string | null;
    ville: string | null;
    pays: string | null;
    code_postal: string | null;
    telephone: string | null;
    mot_de_passe: string;
    role: string;
}

interface Professionnel {
    id: number;
    utilisateur_id: number;
    type_profil: string;
    date_naissance: string;
    telephone: string;
    pays: string;
    pays_exercice: string[];
    langues: string[];
    specialites: string[];
    sous_specialites: string;
    banque: string;
    rib: string;
    code_swift: string;
    numero_ordre_medecins: string;
    honoraire_montant: number;
    honoraire_devise: string;
    info_supplementaires: string;
    agence: string;
    photo_url: string | null; // Optional field for photo URL
}

interface ProfessionalDetails {
    utilisateur: Utilisateur;
    professionnel: Professionnel;
}
const api = (() => {
    const instance = axios.create({ baseURL: baseUrl });
    instance.interceptors.request.use((config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
            config.headers = config.headers || {};
            (config.headers as any).Authorization = `Bearer ${token}`;
        }
        return config;
    });
    return instance;
})();

export const ProfessionalService = {
    async getAllProfessionalsDetails(): Promise<ProfessionalDetails[]> {
        try {
            const response = await api.get<ProfessionalDetails[]>(`/professionnels/details`);
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors de la récupération de la liste des professionnels:', error);
            throw error;
        }
    },

    async getAllProfessionalDetailsByEmail(email: string): Promise<ProfessionalDetails> {
        try {
            const response = await api.get<ProfessionalDetails>(`/professionnels/details/by-email/${email}`);
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors de la récupération de la liste des professionnels:', error);
            throw error;
        }
    },

    async createProfessional(data: CreateProfessionalPayload): Promise<void> {
        // 1. Extract user-related fields for user creation
        const userPayload = {
            nom: data.nom,
            prenom: data.prenom,
            email: data.email,
            mot_de_passe: data.mot_de_passe, //mdp random 
            role: 'professionnel',
        };

        let utilisateur_id: number | undefined;
        try {
            // Create the user first
            const userRes = await api.post(`/api/utilisateurs/`, userPayload);
            utilisateur_id = userRes.data.id;

            if (!utilisateur_id) {
                throw new Error('Utilisateur ID not returned from user creation response.');
            }

           
            // 2. Compose professional creation payload
            const professionalPayload: Professional = {
                utilisateur_id,
                type_profil: data.type_profil,
                specialites: data.specialites,
                sous_specialites: data.sous_specialites,
                numero_ordre_medecins: data.numero_ordre_medecins,
                date_naissance: data.date_naissance,
                pays: data.pays,
                telephone: data.telephone,
                honoraire_montant: data.honoraire_montant,
                honoraire_devise: data.honoraire_devise,
                rib: data.rib,
                banque: data.banque,
                agence: data.agence,
                code_swift: data.code_swift,
                langues: data.langues,
                pays_exercice: data.pays_exercice,
                info_supplementaires: data.info_supplementaires,
            };

            // 3. Send second request to create the professional
            await api.post(`/professionnels/`, professionalPayload);
        } catch (error) {
            // If professional creation fails, delete the user that was just created to avoid orphaned users
            if (utilisateur_id) {
                try {
                    await AuthService.deleteUser(utilisateur_id);
                } catch (deleteError) {
                    // Log if user deletion also fails
                    console.error('Failed to delete user after professional creation failed:', deleteError);
                }
            }
            if (axios.isAxiosError(error)) {
                const detail = error.response?.data?.detail;
                throw detail;
            }
            throw error;
        }
    },

    async getAllProfessionals(): Promise<ProfessionalItem[]> {
        try {
            const response = await api.get<ProfessionalItem[]>(`/professionnels/`);
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors du chargement des professionnels:', error);
            throw error;
        }
    },

    async getPrfessionalInformationByUserId(userId: number): Promise<ProfessionalItem | undefined> {
        try {
            const allProfessionals = await this.getAllProfessionals();
            return allProfessionals.find((item) => item.utilisateur_id === userId);

        } catch (error: unknown) {
            console.error('Erreur lors du chargement des professionnels:', error);
            throw error;
        }
    },

    async deleteProfessionalUser(itemId: number, userId: number): Promise<void> {
        try {
            await this.deleteProfessional(itemId);
            await AuthService.deleteUser(userId);

        } catch (error: unknown) {
            console.error('Erreur lors de la suppression du professionnel:', error);
            throw error;
        }
    },


    async deleteProfessional(itemId: number): Promise<void> {
        try {
            await api.delete(`/professionnels/${itemId}`);
        } catch (error: unknown) {
            console.error('Erreur lors de la suppression du professionnel:', error);
            throw error;
        }
    },

    async UpdateProfessionalDetails(data: any): Promise<any> {
    try {
        const newEmail = data.email;
        const userId = data.utilisateur_id;

        // 1. Check if email is already used by another professional
        try {
            const existing = await this.getAllProfessionalDetailsByEmail(newEmail);

            if (existing.utilisateur.id !== userId) {
                throw new Error('Cet email est déjà utilisé par un autre professionnel.');
            }
        } catch (err: any) {
            if (axios.isAxiosError(err) && err.response?.status === 404) {
                // No user found with this email – safe to continue
            } else {
                throw err; // Any other error must be handled
            }
        }

        // 2. Update user
        const userPayload = {
            nom: data.nom,
            prenom: data.prenom,
            email: data.email,
            code_postal: data.code_postal,
            ville: data.ville,
            adresse: data.adresse,
            nationalite: data.nationalite,
            ...(data.mot_de_passe !== '' && { mot_de_passe: data.mot_de_passe }),
        };
        await AuthService.updateUser(userId, userPayload);

        // 3. Update professional
        const professionalId = data.professionnel_id;
        const professionalPayload = {
            utilisateur_id: userId,
            type_profil: data.type_profil,
            sous_specialites: data.sous_specialites,
            date_naissance: data.date_naissance,
            telephone: data.telephone,
            pays: data.pays,
            pays_exercice: data.pays_exercice,
            langues: data.langues,
            specialites: data.specialites,
            numero_ordre_medecins: data.numero_ordre_medecins,
            adresse: data.adresse,
            ville: data.ville,
            code_postal: data.code_postal,
            rib: data.rib,
            banque: data.banque,
            agence: data.agence,
            code_swift: data.code_swift,
            honoraire_montant: data.honoraire_montant,
            honoraire_devise: data.honoraire_devise,
            info_supplementaires: data.info_supplementaires,
            role: data.role,
            nationalite: data.nationalite,
        };
        await this.updateProfessional(professionalId, professionalPayload);

        return data;
    } catch (error: unknown) {
        console.error('Erreur lors de la mise à jour des détails du professionnel:', error);
        throw error;
    }
},


    async updateProfessional(itemId: number, data: Partial<Professional>): Promise<ProfessionalItem> {
        try {
            const response = await api.put<ProfessionalItem>(`/professionnels/${itemId}`, data);
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors de la mise à jour du professionnel:', error);
            throw error;
        }
    },

    async getProfessionalById(itemId: number): Promise<ProfessionalItem> {
        try {
            const response = await api.get<ProfessionalItem>(`/professionnels/${itemId}`);
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors de la récupération du professionnel:', error);
            throw error;
        }
    },


    // Disponibility management methods
    async updateMultipleDisponibilities(
        professionalId: number,
        disponibilites: Disponibility[]
    ): Promise<any> {

        for (const disponibility of disponibilites) {
            try {
                await this.updateDisponibility(professionalId, disponibility);
                console.log(`✅ Disponibility updated successfully for jour_semaine=${disponibility.jour_semaine}`);
            } catch (error) {
                console.error(`❌ Failed to submit disponibility for jour_semaine=${disponibility.jour_semaine}`, error);
            }
        }
    },
    async submitMultipleDisponibilities(disponibilities: Disponibility[]): Promise<any> {
        for (const disponibility of disponibilities) {
            try {
                await this.createDisponibility(disponibility);
                console.log(`✅ Disponibility submitted for jour_semaine=${disponibility.jour_semaine}`);
            } catch (error) {
                console.error(`❌ Failed to submit disponibility for jour_semaine=${disponibility.jour_semaine}`, error);
            }
        }
    },
    async createDisponibility(disponibility: Disponibility): Promise<any> {
        try {
            const response = await api.post(`/disponibilites/`, disponibility);
            return response.data;
        } catch (error: any) {
            if (error.response) {
                console.error('API Error in createDisponibility:', {
                    status: error.response.status,
                    data: error.response.data,
                });
            }
            throw error;
        }
    },
    async getAllDisponibilities(): Promise<DisponibilityObj[]> {
        try {
            const response = await api.get(`/disponibilites/`);
            return response.data;
        } catch (error: any) {
            if (error.response) {
                console.error('API Error in getting all Disponibilities:', {
                    status: error.response.status,
                    data: error.response.data,
                });
            }
            throw error;
        }
    },
    async getAllDisponibilitiesByProfessionalId(professionalId: number): Promise<DisponibilityObj[]> {
        try {
            const response = await axios.get<DisponibilityObj[]>(`${baseUrl}/disponibilites/by-professionnel?professionnel_id=${professionalId}`);
            return response.data;
        } catch (error: any) {
            if (error.response) {
                console.error('API Error in getting the Disponibilities for a given professional:', {
                    status: error.response.status,
                    data: error.response.data,
                });
            }
            throw error;
        }
    },
    async deleteMultipleDisponibilities(disponibilityIds: number[]): Promise<void> {
        for (const id of disponibilityIds) {
            try {
                await this.deleteDisponibility(id);
                console.log(`Successfully deleted disponibility with ID: ${id}`);
            } catch (error) {
                console.error(`Failed to delete disponibility with ID: ${id}`, error);
            }
        }
    },
    async deleteDisponibility(disponibilityId: number): Promise<void> {
        try {
            await api.delete(`/disponibilites/${disponibilityId}`);
        } catch (error: any) {
            if (error.response) {
                console.error('API Error in deleting Disponibility:', {
                    status: error.response.status,
                    data: error.response.data,
                });
            }
            throw error;
        }
    },
    async updateDisponibility(professionalId: number, disponibilitesObj: Disponibility): Promise<Disponibility> {
        try {
            const response = await api.put<Disponibility>(`/disponibilites/${professionalId}`, disponibilitesObj);
            return response.data;
        } catch (error: any) {
            if (error.response) {
                console.error('API Error in updating Disponibility:', {
                    status: error.response.status,
                    data: error.response.data,
                });
            }
            throw error;
        }
    },
    /**
     * Upload a professional's photo
     * @param {number|string} professionalId - The professional's ID
     * @param {File} file - The image file to upload
     * @returns {Promise<any>} - The response data
     */
    async uploadProfessionalPhoto(professionalId: number | string, file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await api.post(`/upload-photo/professionnel/${professionalId}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error: any) {
            console.error("Erreur lors de l'envoi de la photo du professionnel:", error);
            throw error;
        }
    },
    
    /**
     * TELECONSULTATION SERVICES
     * --------------------------------------------------
     * These methods handle teleconsultation flows:
     * - Fetching specialities
     * - Fetching doctors by speciality
     * - Fetching consultations (rdv) for a doctor
     * - Fetching doctor availabilities (disponibilites)
     * - Creating a new consultation (rdv)
     * Each consultation (rdv) is one hour.
     */

    async getAllSpecialities(): Promise<string[]> {
        try {
            const response = await api.get<string[]>(`/specialites/`);
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors de la récupération des spécialités:', error);
            throw error;
        }
    },

    async getDoctorsBySpeciality(specialite: string): Promise<any[]> {
        try {
            const response = await api.post<any[]>(
                `/professionnels/getAllDoctors`,
                specialite ? { specialite } : {},
                { params: { skip: 0, limit: 50 } },
            );
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors de la récupération des docteurs par spécialité:', error);
            throw error;
        }
    },

    async getAllCoachs(skip = 0, limit = 50): Promise<any[]> {
        try {
            // Endpoint ignores body now; passing no body
            const response = await api.post<any[]>(`/professionnels/getAllCoachs`, undefined, { params: { skip, limit } });
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors de la récupération des coachs:', error);
            throw error;
        }
    },

    async getConsultationsByDoctor(professionnel_id: number): Promise<any[]> {
        try {
            const response = await api.get<any[]>(`/consultations/?professionnel_id=${professionnel_id}`);
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors de la récupération des consultations du docteur:', error);
            throw error;
        }
    },

    /**
     * Get consultations by client email with pagination (POST)
     * @param email - Client email
     * @param skip - Number of consultations to skip (for pagination)
     * @param limit - Number of consultations to fetch (for pagination)
     */
    async getConsultationsByClient(email: string, skip = 0, limit = 10, when: 'upcoming'|'passed'|'all' = 'all'): Promise<any> {
        try {
            const body: any = { email, skip, limit };
            if (when && when !== 'all') body.when = when;
            const response = await api.post(`/consultations/by-email`, body);
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors de la récupération des consultations du client:', error);
            throw error;
        }
    },

    async createConsultation(data: {
        professionnel_id: number;
        date: string;
        heure: string;
        nom_patient: string;
        email: string;
        pays: string;
        telephone: string;
        sujet: string;
    }): Promise<any> {
        try {
            const response = await api.post(`/consultations/`, data);
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors de la création de la consultation:', error);
            throw error;
        }
    },
    /**
     * Professional dashboard overview
     */
    async getProDashboardOverview(professionnelId: number): Promise<{
        stats: { totalPatients: number; totalRevenue: number; totalTime: number; aiRedirects: number; currency?: string };
        upcomingEvents: Array<{ name: string; issue: string; date: string; time?: string }>;
        lastClients: Array<{ name: string; date: string; time?: string }>;
    }> {
        try {
            const response = await api.get(`/pro-dashboard/${professionnelId}/overview`);
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors de la récupération du tableau de bord du professionnel:', error);
            throw error;
        }
    },

    // Separated endpoints
    async getProStats(professionnelId: number): Promise<{ totalPatients: number; totalRevenue: number; totalTime: number; aiRedirects: number; currency?: string }>{
        const { data } = await api.get(`/pro-dashboard/${professionnelId}/stats`);
        return data;
    },
    async getSalesGrowth(professionnelId: number, interval: 'week'|'month'|'year'): Promise<{ interval: string; data: Array<{ period: string; totalRevenue: number; totalTime: number }>}>{
        const { data } = await api.get(`/pro-dashboard/${professionnelId}/sales-growth`, { params: { interval } });
        return data;
    },
    async getUpcomingEvents(professionnelId: number, limit = 7): Promise<Array<{ 
        id: number; 
        name: string; 
        issue?: string; 
        date: string; 
        time?: string; 
        email: string;
        pays?: string;
        telephone?: string;
        image?: { image_url?: string | null; image_base64?: string | null };
        video_consultation?: {
            video_consultation_id: string;
            patient_url: string;
            medic_url: string;
            valid_from: string;
            valid_to: string;
            video_status: string;
        } | null;
        professional?: {
            id: number;
            name: string;
            email: string;
            specialty: string;
            phone?: string;
        };
    }>>{
        const { data } = await api.get(`/pro-dashboard/${professionnelId}/upcoming`, { params: { limit } });
        return data;
    },
    async getLastClients(professionnelId: number, limit = 8): Promise<Array<{ name: string; date: string; time?: string; photoDataUrl?: string }>>{
        const { data } = await api.get(`/pro-dashboard/${professionnelId}/last-clients`, { params: { limit } });
        return data;
    },
};
